// src/lib/time/log-time.ts
import { type IsoDate, monthStartOf } from "@/lib/time/dates";
import { clientSaveContext, isDecimalText, rowKey, sameId } from "@/lib/time/grid";
import {
  type BlockCode,
  evaluateSave,
  needsComment,
  normalizeComment,
  type SaveContext,
  type WarningCode,
} from "@/lib/time/rules";
import type { AssignableTask, PeriodRow, PlanRow, TimeEntryView } from "@/schemas/time";

/**
 * The Log time form's pure side: it runs the same save rules as the grid and the server, so
 * the form's live messages match what a save would answer.
 */

/** Quick-set button text: "15m", "30m", "1h", "8h". */
export function quickHoursLabel(hours: number): string {
  return hours < 1 ? `${hours * 60}m` : `${hours}h`;
}

/** Typed hours: null when empty, NaN when not a plain decimal (as the grid reads it). */
export function parseHoursText(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  return isDecimalText(trimmed) ? Number(trimmed) : Number.NaN;
}

/** The (task, assignment) an existing entry was logged to, if the person can still log to it. */
export function findAssignableFor(
  entry: TimeEntryView,
  assignable: AssignableTask[],
): AssignableTask | null {
  if (!entry.projectTaskId) return null;
  const key = rowKey(entry.projectTaskId, entry.demandPersonId);
  return assignable.find((a) => rowKey(a.projectTaskId, a.demandPersonId) === key) ?? null;
}

/** Whether a picked task is the one the entry is already logged to (same task and assignment). */
export function isSameTask(entry: TimeEntryView, task: AssignableTask): boolean {
  return (
    entry.projectTaskId !== null &&
    rowKey(entry.projectTaskId, entry.demandPersonId) ===
      rowKey(task.projectTaskId, task.demandPersonId)
  );
}

/**
 * The rules' context for the chosen date, read from that date's week. An edit leaves its own
 * hours out of the month only while it stays in its month and on its assignment (another task
 * on the same assignment counts as staying); only then may it pass the plan blocks by not
 * adding hours (as the server decides). The task limit is the save answer's.
 */
export function logTimeContext(args: {
  task: AssignableTask | null;
  date: IsoDate;
  week: { plans: PlanRow[]; periods: PeriodRow[]; thresholdPct: number };
  editing: TimeEntryView | null;
}): SaveContext {
  const { task, date, week, editing } = args;
  const sameAssignment =
    editing !== null && (task === null || sameId(task.demandPersonId, editing.demandPersonId));
  const sameMonth =
    editing !== null && sameAssignment && monthStartOf(editing.date) === monthStartOf(date);
  const ctx = clientSaveContext({
    assignable: task,
    date,
    plans: week.plans,
    periods: week.periods,
    thresholdPct: week.thresholdPct,
    demandPersonId: task?.demandPersonId ?? editing?.demandPersonId ?? null,
    excludeHours: sameMonth ? editing.hours : 0,
  });
  return sameMonth ? { ...ctx, previousHours: editing.hours } : ctx;
}

interface LogTimeMessage {
  code: BlockCode | WarningCode;
  text: string;
  tone: "danger" | "warning";
}

export interface LogTimeCheck {
  /** NaN when empty or unreadable. */
  hours: number;
  /** Blocks first, then warnings. The comment rule is on the Comment label, not here. */
  messages: LogTimeMessage[];
  /** 2 hrs or more on any task: the Comment label reads "(required)". */
  commentRequired: boolean;
  canSave: boolean;
}

const STEP_TEXT = "Use quarter hours";

/** Live messages and whether Save is allowed; `ctx` is null while the date's week loads. */
export function checkLogTime(args: {
  ctx: SaveContext | null;
  raw: string;
  date: IsoDate;
  comment: string;
  today: IsoDate;
}): LogTimeCheck {
  const parsed = parseHoursText(args.raw);
  const hours = parsed ?? Number.NaN;
  if (!args.ctx) return { hours, messages: [], commentRequired: false, canSave: false };

  const verdict = evaluateSave(
    args.ctx,
    { date: args.date, hours, comment: normalizeComment(args.comment) },
    args.today,
  );
  const unreadable = parsed !== null && Number.isNaN(parsed);
  const blocks: LogTimeMessage[] = verdict.blocks
    .filter((b) => b.code !== "comment-required" && !(b.code === "hours-range" && parsed === null))
    .map((b) =>
      unreadable && b.code === "hours-range"
        ? { code: "hours-step", text: STEP_TEXT, tone: "danger" }
        : { ...b, tone: "danger" },
    );
  const warnings: LogTimeMessage[] = verdict.warnings.map((w) => ({ ...w, tone: "warning" }));
  return {
    hours,
    messages: [...blocks, ...warnings],
    commentRequired: needsComment(hours, null),
    canSave: verdict.blocks.length === 0,
  };
}
